import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { FaChartLine } from "react-icons/fa";
import colors from "../styles/colors";
import useAuth from "../hooks/useAuth";
import { tableHeadCellSx, COLORS } from "../styles/uiConstants";
import { periodOptions, reportTemplates } from "../utils/reportTemplates";
import { useRunReportMutation } from "../store/reportsApi";
import {
  ActionButton,
  FormDatePicker,
  FormSelect,
  SearchField,
} from "../components/atoms";
import { DataTableCard } from "../components/organisms";

export default function ReportDetail() {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const { auth } = useAuth();
  const token = auth?.accessToken;

  const report = useMemo(
    () => reportTemplates.find((item) => item.id === reportId),
    [reportId]
  );

  const [period, setPeriod] = useState(
    report?.defaultPeriodValue || "last30"
  );
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 900
  );

  const [runReport, { data: reportData, isLoading, error }] =
    useRunReportMutation();

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  React.useEffect(() => {
    if (!report) return;
    setPeriod(report.defaultPeriodValue || "last30");
    setPage(0);
    setSearch("");
    setDateFrom("");
    setDateTo("");
  }, [report]);

  const resolveDateRange = () => {
    if (period === "custom") {
      return {
        startDate: dateFrom || undefined,
        endDate: dateTo || undefined,
      };
    }

    const end = new Date();
    const start = new Date(end);
    if (period === "last7") start.setDate(end.getDate() - 7);
    if (period === "last30") start.setDate(end.getDate() - 30);
    if (period === "last90") start.setDate(end.getDate() - 90);
    if (period === "last6m") start.setMonth(end.getMonth() - 6);
    if (period === "thisMonth") start.setDate(1);

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  };

  const canSearch =
    period !== "custom" || (Boolean(dateFrom) && Boolean(dateTo));

  const submitSearch = async (nextPage = 0, nextLimit = rowsPerPage) => {
    if (!token || !report) return;
    if (!canSearch) return;

    const { startDate, endDate } = resolveDateRange();
    try {
      await runReport({
        token,
        reportId: report.id,
        startDate,
        endDate,
        query: search.trim() || undefined,
        page: nextPage + 1,
        limit: nextLimit,
      }).unwrap();
    } catch (e) {
      // error state handled by RTK query
    }
  };

  const tableHeight = useMemo(() => {
    const rowHeight = 44;
    const headHeight = 44;
    const desired = headHeight + rowsPerPage * rowHeight;
    const available = Math.max(240, viewportHeight - 440);
    return Math.min(desired, available);
  }, [rowsPerPage, viewportHeight]);

  if (!report) {
    return (
      <Box
        sx={{
          height: "100%",
          background: `linear-gradient(135deg, ${COLORS.slate50} 0%, ${COLORS.slate200} 100%)`,
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Card
          sx={{
            maxWidth: 720,
            mx: "auto",
            p: 3,
            borderRadius: 4,
            border: `1px solid ${COLORS.slate200}`,
          }}
        >
          <Alert severity="error" sx={{ mb: 2 }}>
            Rapor bulunamadı. Lütfen listeden tekrar seçim yapın.
          </Alert>
          <ActionButton
            palette={colors}
            size="medium"
            label="Raporlar’a Dön"
            startIcon={<ArrowBackIcon sx={{ fontSize: 15 }} />}
            onClick={() => navigate("/Raporlar")}
          />
        </Card>
      </Box>
    );
  }

  const columns = report.columns || [];
  const dataRows = reportData?.data?.rows || [];
  const pagination = reportData?.data?.pagination;
  const totalRows = pagination?.total ?? dataRows.length;

  return (
    <Box
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${COLORS.slate50} 0%, ${COLORS.slate200} 100%)`,
        p: { xs: 2, sm: 3, md: 4 },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          maxWidth: "1400px",
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          height: "100%",
          minHeight: 0,
        }}
      >
        <Card
          sx={{
            borderRadius: 4,
            p: 3,
            background: `linear-gradient(135deg, ${COLORS.white} 0%, ${COLORS.slate50} 100%)`,
            boxShadow: "0 14px 40px rgba(15,23,42,0.08)",
            border: `1px solid ${COLORS.slate200}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/Raporlar")}
                sx={{
                  borderRadius: 2,
                  fontWeight: 800,
                  textTransform: "none",
                  borderColor: COLORS.slate200,
                  color: "text.secondary",
                  "&:hover": {
                    borderColor: colors.primary,
                    bgcolor: "rgba(37,99,235,0.06)",
                  },
                }}
              >
                Raporlar
              </Button>
              <Box
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  flexShrink: 0,
                  boxShadow: "0 10px 24px rgba(37,99,235,0.35)",
                }}
              >
                <FaChartLine size={18} />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 900, color: colors.primary, lineHeight: 1.2 }}
                >
                  {report.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontWeight: 600 }}
                >
                  {report.description}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                label={report.category}
                size="small"
                sx={{
                  bgcolor: "rgba(37,99,235,0.12)",
                  color: colors.primaryDark,
                  fontWeight: 800,
                  borderRadius: 2,
                }}
              />
              <Chip
                label={report.scope === "system" ? "Sistem" : "Klinik"}
                size="small"
                sx={{
                  bgcolor: report.scope === "system" ? COLORS.catBlueLight : COLORS.successLight,
                  color: report.scope === "system" ? COLORS.catBlueDark : COLORS.successDarker,
                  fontWeight: 800,
                  borderRadius: 2,
                }}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <FormSelect
              label="Dönem"
              value={period}
              onChange={setPeriod}
              options={periodOptions}
              placeholder={null}
              fullWidth={false}
              sx={{ minWidth: 200 }}
            />

            {period === "custom" ? (
              <>
                <FormDatePicker
                  label="Başlangıç"
                  value={dateFrom}
                  onChange={setDateFrom}
                  maxDate={dateTo || undefined}
                  sx={{ minWidth: 160 }}
                />
                <FormDatePicker
                  label="Bitiş"
                  value={dateTo}
                  onChange={setDateTo}
                  minDate={dateFrom || undefined}
                  sx={{ minWidth: 160 }}
                />
              </>
            ) : null}

            <SearchField
              label="Ara"
              value={search}
              onChange={(v) => {
                setSearch(v);
                setPage(0);
              }}
              sx={{ flex: "1 1 240px", minWidth: 220, maxWidth: 360 }}
            />
            <ActionButton
              palette={colors}
              size="medium"
              label="Ara"
              loading={isLoading}
              disabled={!token || !canSearch}
              onClick={() => submitSearch(0, rowsPerPage)}
              {...(!isLoading ? { startIcon: <SearchIcon sx={{ fontSize: 15 }} /> } : {})}
            />
          </Box>
        </Card>

        {error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            {error?.data?.message || "Rapor verisi alınamadı."}
          </Alert>
        ) : null}

        <DataTableCard
      loading={Boolean(isLoading)}
          tableHeight={tableHeight}
          empty={!isLoading && dataRows.length === 0}
          emptyIcon={<FaChartLine size={26} />}
          emptyTitle={report.emptyTitle}
          emptyMessage={report.emptyDescription}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalRows}
          onPageChange={(e, newPage) => {
            setPage(newPage);
            submitSearch(newPage, rowsPerPage);
          }}
          onRowsPerPageChange={(e) => {
            const nextLimit = parseInt(e.target.value, 10);
            setRowsPerPage(nextLimit);
            setPage(0);
            submitSearch(0, nextLimit);
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    align={col.align || "left"}
                    sx={{ ...tableHeadCellSx, width: col.width }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                [...Array(Math.min(8, rowsPerPage))].map((_, idx) => (
                  <TableRow key={`sk-${idx}`}>
                    {columns.map((col) => (
                      <TableCell key={col.key} align={col.align || "left"}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : dataRows.length ? (
                dataRows.map((row, idx) => (
                  <TableRow key={idx}>
                    {columns.map((col) => (
                      <TableCell key={col.key} align={col.align || "left"}>
                        {row[col.key] ?? "-"}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ height: 1, py: 0 }} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DataTableCard>
      </Box>
    </Box>
  );
}
